// components/chat-attachments/chat-attachments.component.jsx
import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  IoChevronBack,
  IoChevronForward,
  IoClose,
  IoDocumentAttachOutline,
  IoLocationOutline,
  IoPlay,
} from "react-icons/io5";

import Peep from "../avatar-creator/Peep";

const formatBytes = (bytes) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return "";

  const units = ["B", "KB", "MB", "GB"];
  const unitIndex = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  );
  const value = bytes / 1024 ** unitIndex;

  return `${
    value >= 10 || unitIndex === 0 ? value.toFixed(0) : value.toFixed(1)
  } ${units[unitIndex]}`;
};

const getLocationUrl = (attachment) => {
  if (!Number.isFinite(attachment?.latitude)) return null;
  if (!Number.isFinite(attachment?.longitude)) return null;

  return `https://www.google.com/maps/search/?api=1&query=${attachment.latitude},${attachment.longitude}`;
};

export function ChatMessageAttachments({
  attachments = [],
  stickerAvatar,
}) {
  const mediaAttachments = useMemo(
    () =>
      attachments.filter(
        (attachment) =>
          ["image", "video", "gif"].includes(attachment.kind) &&
          attachment.url,
      ),
    [attachments],
  );

  const [lightboxIndex, setLightboxIndex] = useState(null);
  const activeMedia =
    lightboxIndex === null ? null : mediaAttachments[lightboxIndex];

  useEffect(() => {
    if (!activeMedia) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setLightboxIndex(null);
      }

      if (event.key === "ArrowLeft") {
        setLightboxIndex((currentIndex) =>
          currentIndex === null
            ? null
            : (currentIndex - 1 + mediaAttachments.length) %
              mediaAttachments.length,
        );
      }

      if (event.key === "ArrowRight") {
        setLightboxIndex((currentIndex) =>
          currentIndex === null
            ? null
            : (currentIndex + 1) % mediaAttachments.length,
        );
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeMedia, mediaAttachments.length]);

  if (!Array.isArray(attachments) || attachments.length === 0) return null;

  const openMedia = (attachment) => {
    const index = mediaAttachments.findIndex(
      (mediaItem) => mediaItem.id === attachment.id,
    );

    if (index >= 0) setLightboxIndex(index);
  };

  return (
    <>
      <div
        className={`ch__messageAttachments ch__messageAttachments-${Math.min(
          attachments.length,
          4,
        )}`}
      >
        {attachments.map((attachment) => {
          if (attachment.kind === "image" && attachment.url) {
            return (
              <button
                type="button"
                key={attachment.id}
                className="ch__messageAttachment-media"
                onClick={() => openMedia(attachment)}
              >
                <img src={attachment.url} alt={attachment.name || "Shared"} />
              </button>
            );
          }

          if (attachment.kind === "gif" && attachment.url) {
            return (
              <button
                type="button"
                key={attachment.id}
                className="ch__messageAttachment-media ch__messageAttachment-gif"
                onClick={() => openMedia(attachment)}
                title={attachment.title || "GIF"}
              >
                <img src={attachment.url} alt={attachment.title || "GIF"} />
              </button>
            );
          }

          if (attachment.kind === "sticker" && attachment.key) {
            return (
              <div
                key={attachment.id}
                className="ch__messageAttachment-sticker"
                title={attachment.key}
              >
                <Peep
                  useravatar={stickerAvatar}
                  size={130}
                  width={130}
                  height={160}
                  borderRadius={8}
                  borderWidth={0}
                  overflow="hidden"
                  backgroundColor="#F5F5F5"
                  reaction={attachment.key}
                />
              </div>
            );
          }

          if (attachment.kind === "video" && attachment.url) {
            return (
              <button
                type="button"
                key={attachment.id}
                className="ch__messageAttachment-media ch__messageAttachment-video"
                onClick={() => openMedia(attachment)}
              >
                {attachment.thumbUrl ? (
                  <img src={attachment.thumbUrl} alt="Video cover" />
                ) : (
                  <video src={attachment.url} muted preload="metadata" />
                )}
                <span className="ch__messageAttachment-play">
                  <IoPlay />
                </span>
              </button>
            );
          }

          if (attachment.kind === "file" && attachment.url) {
            return (
              <a
                key={attachment.id}
                className="ch__messageAttachment-file"
                href={attachment.url}
                target="_blank"
                rel="noreferrer"
              >
                <IoDocumentAttachOutline />
                <span>
                  <strong>{attachment.name || "Shared file"}</strong>
                  {attachment.size ? (
                    <small>{formatBytes(attachment.size)}</small>
                  ) : null}
                </span>
              </a>
            );
          }

          if (attachment.kind === "location") {
            const locationUrl = getLocationUrl(attachment);

            return (
              <a
                key={attachment.id}
                className="ch__messageAttachment-location"
                href={locationUrl || undefined}
                target={locationUrl ? "_blank" : undefined}
                rel={locationUrl ? "noreferrer" : undefined}
                onClick={(event) => {
                  if (!locationUrl) event.preventDefault();
                }}
              >
                <IoLocationOutline />
                <span>
                  <strong>{attachment.title || "Shared location"}</strong>
                  <small>
                    {attachment.subtitle ||
                      `${attachment.latitude?.toFixed?.(5)}, ${attachment.longitude?.toFixed?.(5)}`}
                  </small>
                </span>
              </a>
            );
          }

          return null;
        })}
      </div>

      {activeMedia &&
        createPortal(
          <div
            className="ch__attachmentLightbox"
            role="dialog"
            aria-modal="true"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                setLightboxIndex(null);
              }
            }}
          >
            <button
              type="button"
              className="ch__attachmentLightbox-close"
              onClick={() => setLightboxIndex(null)}
              aria-label="Close media"
            >
              <IoClose />
            </button>

            {mediaAttachments.length > 1 && (
              <button
                type="button"
                className="ch__attachmentLightbox-previous"
                onClick={() =>
                  setLightboxIndex(
                    (lightboxIndex - 1 + mediaAttachments.length) %
                      mediaAttachments.length,
                  )
                }
                aria-label="Previous media"
              >
                <IoChevronBack />
              </button>
            )}

            <div className="ch__attachmentLightbox-content">
              {activeMedia.kind === "video" ? (
                <video
                  key={activeMedia.id}
                  src={activeMedia.url}
                  poster={activeMedia.thumbUrl || undefined}
                  controls
                  autoPlay
                />
              ) : (
                <img
                  src={activeMedia.url}
                  alt={activeMedia.title || activeMedia.name || "Shared"}
                />
              )}
            </div>

            {mediaAttachments.length > 1 && (
              <button
                type="button"
                className="ch__attachmentLightbox-next"
                onClick={() =>
                  setLightboxIndex(
                    (lightboxIndex + 1) % mediaAttachments.length,
                  )
                }
                aria-label="Next media"
              >
                <IoChevronForward />
              </button>
            )}
          </div>,
          document.body,
        )}
    </>
  );
}

export function PendingChatAttachments({
  attachments = [],
  uploadMap = {},
  onMove,
  onRemove,
}) {
  if (attachments.length === 0) return null;

  return (
    <div className="ch__pendingAttachments">
      {attachments.map((attachment, index) => {
        const upload = uploadMap[attachment.id];
        const progress =
          upload?.phase === "processing" ? 0.12 : upload?.progress || 0;

        return (
          <div className="ch__pendingAttachment" key={attachment.id}>
            <div className="ch__pendingAttachment-controls">
              <span>{index + 1}</span>
              <button
                type="button"
                onClick={() => onMove(attachment.id, "left")}
                disabled={index === 0}
                aria-label="Move attachment left"
              >
                <IoChevronBack />
              </button>
              <button
                type="button"
                onClick={() => onMove(attachment.id, "right")}
                disabled={index === attachments.length - 1}
                aria-label="Move attachment right"
              >
                <IoChevronForward />
              </button>
              <button
                type="button"
                onClick={() => onRemove(attachment.id)}
                aria-label="Remove attachment"
              >
                <IoClose />
              </button>
            </div>

            <div className="ch__pendingAttachment-preview">
              {attachment.kind === "image" ? (
                <img src={attachment.localUrl} alt="Selected" />
              ) : attachment.kind === "gif" ? (
                <>
                  <img
                    src={attachment.previewUrl || attachment.url}
                    alt={attachment.title || "GIF"}
                  />
                  <span className="ch__pendingAttachment-videoBadge">GIF</span>
                </>
              ) : attachment.kind === "video" ? (
                <>
                  <video src={attachment.localUrl} muted preload="metadata" />
                  <span className="ch__pendingAttachment-videoBadge">
                    VIDEO
                  </span>
                </>
              ) : attachment.kind === "location" ? (
                <>
                  <IoLocationOutline />
                  <span>{attachment.title || "Shared location"}</span>
                </>
              ) : (
                <>
                  <IoDocumentAttachOutline />
                  <span>{attachment.name || "File"}</span>
                </>
              )}
            </div>

            {upload && (
              <div className="ch__pendingAttachment-progress">
                <span style={{ width: `${Math.max(2, progress * 100)}%` }} />
                <small>
                  {upload.phase === "processing"
                    ? "processing"
                    : upload.phase === "uploading"
                      ? `${Math.round(progress * 100)}%`
                      : upload.phase}
                </small>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function LocationAttachmentModal({ location, onCancel, onConfirm }) {
  const [title, setTitle] = useState("Shared location");
  const [subtitle, setSubtitle] = useState("");

  useEffect(() => {
    if (!location) return;

    setTitle("Shared location");
    setSubtitle(
      `${location.latitude.toFixed(5)}, ${location.longitude.toFixed(5)}`,
    );
  }, [location]);

  if (!location) return null;

  return createPortal(
    <div className="ch__locationModal" role="dialog" aria-modal="true">
      <div className="ch__locationModal-card">
        <div className="ch__locationModal-heading">
          <IoLocationOutline />
          <div>
            <strong>Share location</strong>
            <small>
              {location.latitude.toFixed(5)}, {location.longitude.toFixed(5)}
            </small>
          </div>
        </div>

        <label>
          Title
          <input
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            maxLength={80}
          />
        </label>

        <label>
          Description
          <input
            value={subtitle}
            onChange={(event) => setSubtitle(event.target.value)}
            maxLength={160}
          />
        </label>

        <div className="ch__locationModal-actions">
          <button type="button" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            onClick={() =>
              onConfirm({
                ...location,
                title: title.trim() || "Shared location",
                subtitle: subtitle.trim(),
              })
            }
          >
            Add location
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
